/**
 * Guest capability gating for the chat client.
 *
 * MIRRORS backend/src/utils/guestCaps.js (GUEST_CAPS / isGuestRole / guestCan).
 * The server is authoritative — this copy exists only so the UI can hide
 * controls the server would refuse anyway. Keep the two in sync.
 *
 * Guests are anonymous throwaway identities, so three capabilities are denied to
 * them unless the tenant opts in: image upload, peer-to-peer file transfer, and
 * private (IM) chat. Semantics are default-OFF — a missing flag denies.
 *
 * Roles arrive in mixed case: the socket presence payload lowercases them
 * ("guest") while the REST membership keeps them uppercase ("GUEST"), so every
 * comparison is case-insensitive.
 */

/** Capability name -> the key the tenant config exposes under `features`. */
export const GUEST_CAPS = {
  images: "guestsCanUploadImages",
  files: "guestsCanSendFiles",
  privateChat: "guestsCanPrivateChat",
}

/**
 * Is this a GUEST role? Unknown/missing roles are NOT treated as guest — an
 * unknown value means the caller read the wrong field, and silently gating a
 * member would be a confusing failure.
 * @param {string|null|undefined} role
 * @returns {boolean}
 */
export const isGuestRole = (role) =>
  String(role || "").trim().toUpperCase() === "GUEST"

/**
 * May a guest on this tenant use `cap`?
 * @param {object|null|undefined} features - tenantConfig.features
 * @param {'images'|'files'|'privateChat'} cap
 * @returns {boolean}
 */
export const guestCan = (features, cap) => {
  const key = GUEST_CAPS[cap]
  if (!key) return false // unknown capability -> deny (fail-closed)
  return !!(features && features[key])
}

/**
 * May *I* use `cap`? The common case — members always may; guests only when the
 * tenant opted in.
 *
 * @param {object|null|undefined} features - tenantConfig.features
 * @param {boolean} iAmGuest
 * @param {'images'|'files'|'privateChat'} cap
 * @returns {boolean}
 */
export const canUseCap = (features, iAmGuest, cap) =>
  !iAmGuest || guestCan(features, cap)

/**
 * May I use `cap` *with this person*? For the two-sided capabilities (files,
 * private chat) neither party may be a guest — mirrors the server's `guestBlocks`.
 *
 * @param {object|null|undefined} features - tenantConfig.features
 * @param {boolean} iAmGuest
 * @param {string|null|undefined} targetRole - the other user's role
 * @param {'files'|'privateChat'} cap
 * @returns {boolean}
 */
export const canUseCapWith = (features, iAmGuest, targetRole, cap) => {
  if (!iAmGuest && !isGuestRole(targetRole)) return true
  return guestCan(features, cap)
}
